import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import Swal from 'sweetalert2';

import { AtelierService } from 'app/services/atelier/atelier.service';
import { ServicesService } from 'app/services/services/services.service';
import { AwscontrolService, UploadResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';
import { Atelier } from 'app/models/atelier';

type ImageUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-edit-atelier',
  templateUrl: './edit-atelier.component.html',
  styleUrls: ['./edit-atelier.component.scss']
})
export class EditAtelierComponent implements OnInit {
  // Atelier data
  atelier: Atelier = {
    name: '',
    namearabe: '',
    nameanglais: '',
    prix: '',
    description: '',
    descriptionarabe: '',
    descriptionanglais: '',
    mainImageUrl: '',
    galleryImages: [],
    openingTime: '',
    closingTime: '',
    activeDays: [],
    sessionDurations: [],
    serviceId: 0
  };

  service: any;
  idAtelier: string | null = null;
  idService: string | null = null;

  agencyAvailableLanguages: string[] = ['FR'];

  // ==================== MAIN IMAGE ====================
  mainImage: string | null = null;
  mainImageFile: File | null = null;
  isUploadingMain: boolean = false;

  mainImageUploadMode: ImageUploadMode = 'direct';
  mainPreviewOriginalUrl: string | null = null;
  mainPreviewProcessedUrl: string | null = null;
  isAwaitingMainValidation: boolean = false;

  // ==================== GALLERY ====================
  galleryImages: string[] = [];
  selectedGalleryFiles: FileList | null = null;
  isUploadingGallery: boolean = false;

  galleryUploadMode: ImageUploadMode = 'direct';
  galleryPreviewOriginalUrls: string[] = [];
  galleryPreviewProcessedUrls: string[] = [];
  isAwaitingGalleryValidation: boolean = false;

  // Form state
  isSubmitting: boolean = false;
  isLoading: boolean = true;

  // Custom duration
  customDurationInput: number | null = null;

  // Days of week
  daysOfWeek = [
    { value: 1, label: 'Lundi' },
    { value: 2, label: 'Mardi' },
    { value: 3, label: 'Mercredi' },
    { value: 4, label: 'Jeudi' },
    { value: 5, label: 'Vendredi' },
    { value: 6, label: 'Samedi' },
    { value: 7, label: 'Dimanche' }
  ];

  predefinedDurations = [
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '1 heure' },
    { value: 90, label: '1h30' },
    { value: 120, label: '2 heures' },
    { value: 180, label: '3 heures' },
    { value: 240, label: '4 heures' }
  ];

  dayChecks: boolean[] = [false, false, false, false, false, false, false];
  durationChecks: boolean[] = [];

  @ViewChild('mainImageInput') mainImageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('galleryFileInput') galleryFileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private atelierService: AtelierService,
    private serviceService: ServicesService,
    private awsService: AwscontrolService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.durationChecks = this.predefinedDurations.map(() => false);
  }

  ngOnInit(): void {
    this.idAtelier = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.idAtelier) {
      this.loadAtelier(parseInt(this.idAtelier, 10));
    } else {
      Swal.fire('Erreur', 'ID atelier manquant', 'error');
      this.router.navigate(['/listeServices']);
    }
  }

  loadAtelier(id: number): void {
    this.isLoading = true;
    this.atelierService.getAtelier(id).subscribe({
      next: (data: any) => {
        this.atelier = data;
        this.atelier.serviceId = data.service?.id || 0;
        this.mainImage = data.mainImageUrl || null;
        this.galleryImages = data.galleryImages || [];
        this.idService = this.atelier.serviceId.toString();

        // Load Arabic fields
        this.atelier.namearabe = data.namearabe || '';
        this.atelier.nameanglais = data.nameanglais || '';
        this.atelier.descriptionarabe = data.descriptionarabe || '';
        this.atelier.descriptionanglais = data.descriptionanglais || '';

        // Populate chip states
        if (data.activeDays) {
          this.dayChecks = this.daysOfWeek.map(day => data.activeDays.includes(day.value));
        }
        if (data.sessionDurations) {
          this.durationChecks = this.predefinedDurations.map(dur => data.sessionDurations.includes(dur.value));
        }

        // Load parent service
        if (this.idService) {
          this.serviceService.getService(this.idService).subscribe({
            next: (svc: any) => {
              this.service = svc;
              if (svc.user?.availableLanguages && Array.isArray(svc.user.availableLanguages)) {
                this.agencyAvailableLanguages = svc.user.availableLanguages;
              } else {
                this.agencyAvailableLanguages = ['FR'];
              }
            },
            error: () => { /* silence */ }
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Erreur chargement atelier:', err);
        Swal.fire('Erreur', 'Impossible de charger l\'atelier', 'error');
        this.isLoading = false;
        this.router.navigate(['/listeServices']);
      }
    });
  }

  // ==================== TEXT FORMATTING ====================
  formatText(command: string): void {
    if (!this.atelier.description) this.atelier.description = '';
    const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.atelier.description.substring(start, end);
    let formattedText = '';
    switch (command) {
      case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic': formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
      case 'paragraph': formattedText = `<p>${selectedText}</p>`; break;
      case 'list':
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
      case 'h1': formattedText = `<h1>${selectedText}</h1>`; break;
      case 'h2': formattedText = `<h2>${selectedText}</h2>`; break;
      case 'linebreak': formattedText = '</br>'; break;
    }
    const newText = this.atelier.description.substring(0, start) + formattedText + this.atelier.description.substring(end);
    this.atelier.description = newText;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  formatTextArabic(command: string): void {
    if (!this.atelier.descriptionarabe) this.atelier.descriptionarabe = '';
    const textarea = document.getElementById('descriptionTextareaArabic') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.atelier.descriptionarabe.substring(start, end);
    let formattedText = '';
    switch (command) {
      case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic': formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
      case 'paragraph': formattedText = `<p>${selectedText}</p>`; break;
      case 'list':
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
      case 'h1': formattedText = `<h1>${selectedText}</h1>`; break;
      case 'h2': formattedText = `<h2>${selectedText}</h2>`; break;
      case 'linebreak': formattedText = '</br>'; break;
    }
    const newText = this.atelier.descriptionarabe.substring(0, start) + formattedText + this.atelier.descriptionarabe.substring(end);
    this.atelier.descriptionarabe = newText;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  formatTextEnglish(command: string): void {
    if (!this.atelier.descriptionanglais) this.atelier.descriptionanglais = '';
    const textarea = document.getElementById('descriptionTextareaEnglish') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.atelier.descriptionanglais.substring(start, end);
    let formattedText = '';
    switch (command) {
      case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic': formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
      case 'paragraph': formattedText = `<p>${selectedText}</p>`; break;
      case 'list':
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
      case 'h1': formattedText = `<h1>${selectedText}</h1>`; break;
      case 'h2': formattedText = `<h2>${selectedText}</h2>`; break;
      case 'linebreak': formattedText = '</br>'; break;
    }
    const newText = this.atelier.descriptionanglais.substring(0, start) + formattedText + this.atelier.descriptionanglais.substring(end);
    this.atelier.descriptionanglais = newText;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  // ==================== CHIP TOGGLES ====================
  toggleDay(index: number): void {
    if (!this.atelier.activeDays) this.atelier.activeDays = [];
    this.dayChecks[index] = !this.dayChecks[index];
    const dayValue = this.daysOfWeek[index].value;
    if (this.dayChecks[index]) {
      if (!this.atelier.activeDays.includes(dayValue)) {
        this.atelier.activeDays.push(dayValue);
      }
    } else {
      const pos = this.atelier.activeDays.indexOf(dayValue);
      if (pos > -1) {
        this.atelier.activeDays.splice(pos, 1);
      }
    }
  }

  toggleDuration(index: number): void {
    if (!this.atelier.sessionDurations) this.atelier.sessionDurations = [];
    this.durationChecks[index] = !this.durationChecks[index];
    const durationValue = this.predefinedDurations[index].value;
    if (this.durationChecks[index]) {
      if (!this.atelier.sessionDurations.includes(durationValue)) {
        this.atelier.sessionDurations.push(durationValue);
      }
    } else {
      const pos = this.atelier.sessionDurations.indexOf(durationValue);
      if (pos > -1) {
        this.atelier.sessionDurations.splice(pos, 1);
      }
    }
  }

  addCustomDuration(): void {
    if (!this.atelier.sessionDurations) this.atelier.sessionDurations = [];
    if (!this.customDurationInput || this.customDurationInput <= 0) {
      Swal.fire('Valeur invalide', 'Veuillez saisir un nombre positif (en minutes)', 'warning');
      return;
    }
    if (this.atelier.sessionDurations.includes(this.customDurationInput)) {
      Swal.fire('Déjà présent', 'Cette durée est déjà sélectionnée.', 'info');
      return;
    }
    this.atelier.sessionDurations.push(this.customDurationInput);
    this.customDurationInput = null;
  }

  removeDuration(duration: number): void {
    if (!this.atelier.sessionDurations) return;
    const index = this.atelier.sessionDurations.indexOf(duration);
    if (index > -1) {
      this.atelier.sessionDurations.splice(index, 1);
    }
    const predIndex = this.predefinedDurations.findIndex(d => d.value === duration);
    if (predIndex > -1) {
      this.durationChecks[predIndex] = false;
    }
  }

  getDurationLabel(duration: number): string {
    if (duration < 60) return `${duration} min`;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (minutes === 0) return `${hours} heure${hours > 1 ? 's' : ''}`;
    return `${hours}h${minutes}`;
  }

  // ==================== VALIDATION UTILITAIRE ====================
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire('Format non supporté', 'Veuillez sélectionner une image (JPEG, PNG, JPG, GIF, WEBP)', 'warning');
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire('Fichier trop volumineux', 'La taille maximale est de 5MB', 'warning');
      return false;
    }
    return true;
  }

  // ============================================================
  // ==================== MAIN IMAGE ============================
  // ============================================================

  setMainUploadMode(mode: ImageUploadMode): void {
    if (this.isUploadingMain) return;
    if (this.isAwaitingMainValidation) {
      this.cancelMainImagePreview(true);
    }
    this.mainImageUploadMode = mode;
    console.log('🔄 Mode image principale :', mode);
  }

  triggerMainImageUpload(): void {
    if (!this.isUploadingMain && !this.isAwaitingMainValidation) {
      this.mainImageInput.nativeElement.click();
    }
  }

  onMainImageSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files && target.files[0];
    if (!file) return;
    if (!this.validateImageFile(file)) {
      target.value = '';
      return;
    }
    this.mainImageFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.mainPreviewOriginalUrl = (e.target?.result as string) || null;
    };
    reader.readAsDataURL(file);

    if (this.mainImageUploadMode === 'direct') {
      this.uploadMainImage();
    } else {
      this.uploadMainImageViaImagesousproduit();
    }
  }

  uploadMainImage(): void {
    if (!this.mainImageFile) return;
    this.isUploadingMain = true;

    this.awsService.uploadFile(this.mainImageFile, 'atelier-main-images').subscribe({
      next: (response: UploadResponse) => {
        this.isUploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainImage = response.urls[0];
          this.atelier.mainImageUrl = this.mainImage;
          this.mainPreviewOriginalUrl = null;
          Swal.fire({
            icon: 'success',
            title: 'Image principale mise à jour',
            timer: 1500,
            showConfirmButton: false
          });
          if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
          this.mainImageFile = null;
        } else {
          Swal.fire('Erreur', response.error || 'Échec du téléchargement', 'error');
        }
      },
      error: (err) => {
        this.isUploadingMain = false;
        console.error('Erreur upload image principale:', err);
        Swal.fire('Erreur', 'Impossible de télécharger l\'image principale', 'error');
        this.mainPreviewOriginalUrl = null;
      }
    });
  }

  uploadMainImageViaImagesousproduit(): void {
    if (!this.mainImageFile) return;
    this.isUploadingMain = true;
    this.isAwaitingMainValidation = false;
    this.mainPreviewProcessedUrl = null;

    this.awsService.imagesousproduit([this.mainImageFile], 'atelier-main-images').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainPreviewProcessedUrl = response.urls[0];
          this.isAwaitingMainValidation = true;
        } else {
          Swal.fire('Erreur', (response.errors && response.errors[0]) || 'Échec du traitement', 'error');
          this.mainPreviewOriginalUrl = null;
        }
      },
      error: () => {
        this.isUploadingMain = false;
        Swal.fire('Erreur', 'Impossible de traiter l\'image principale', 'error');
        this.mainPreviewOriginalUrl = null;
      }
    });
  }

  validateMainImagePreview(): void {
    if (!this.mainPreviewProcessedUrl) return;

    this.mainImage = this.mainPreviewProcessedUrl;
    this.atelier.mainImageUrl = this.mainPreviewProcessedUrl;

    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;
    this.mainImageFile = null;

    if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';

    Swal.fire({
      icon: 'success',
      title: 'Image validée',
      text: 'L\'image principale a été acceptée.',
      timer: 1500,
      showConfirmButton: false
    });
  }

  cancelMainImagePreview(silent: boolean = false): void {
    const urlToDelete = this.mainPreviewProcessedUrl;

    const doCancel = () => {
      if (urlToDelete) {
        this.awsService.deleteFile(urlToDelete).subscribe({
          next: () => console.log('🗑️ Image principale refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      }
      this.mainPreviewOriginalUrl = null;
      this.mainPreviewProcessedUrl = null;
      this.isAwaitingMainValidation = false;
      this.mainImageFile = null;

      if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser cette image ?',
      text: 'L\'image traitée sera supprimée et vous pourrez en choisir une autre.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  removeMainImage(): void {
    if (this.isAwaitingMainValidation) {
      this.cancelMainImagePreview();
      return;
    }
    if (!this.mainImage) return;

    Swal.fire({
      title: 'Supprimer l\'image principale ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, supprimer'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await this.awsService.deleteFile(this.mainImage!).toPromise();
          this.mainImage = null;
          this.atelier.mainImageUrl = '';
          Swal.fire('Supprimée', '', 'success');
        } catch (error) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'image', 'error');
        }
      }
    });
  }

  // ============================================================
  // ==================== GALERIE ===============================
  // ============================================================

  setGalleryUploadMode(mode: ImageUploadMode): void {
    if (this.isUploadingGallery) return;
    if (this.isAwaitingGalleryValidation) {
      this.cancelGalleryPreview(true);
    }
    this.galleryUploadMode = mode;
    console.log('🔄 Mode galerie :', mode);
  }

  triggerGalleryUpload(): void {
    if (!this.isUploadingGallery && !this.isAwaitingGalleryValidation) {
      this.galleryFileInput.nativeElement.click();
    }
  }

  onGalleryFilesSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (this.validateImageFile(files[i])) {
        validFiles.push(files[i]);
      }
    }

    if (validFiles.length === 0) {
      target.value = '';
      return;
    }

    this.selectedGalleryFiles = target.files;

    this.galleryPreviewOriginalUrls = [];
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = (e.target?.result as string) || '';
        if (url) this.galleryPreviewOriginalUrls.push(url);
      };
      reader.readAsDataURL(file);
    });

    if (this.galleryUploadMode === 'direct') {
      this.uploadGalleryImages(validFiles);
    } else {
      this.uploadGalleryImagesViaImagesousproduit(validFiles);
    }

    target.value = '';
  }

  uploadGalleryImages(files: File[]): void {
    if (!files || files.length === 0) return;
    this.isUploadingGallery = true;

    Swal.fire({
      title: 'Téléchargement en cours...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    this.awsService.uploadFiles(files, 'atelier-gallery-images').subscribe({
      next: (response: UploadResponse) => {
        this.isUploadingGallery = false;
        Swal.close();
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryImages.push(...response.urls);
          this.atelier.galleryImages = this.galleryImages;
          this.galleryPreviewOriginalUrls = [];
          Swal.fire({
            icon: 'success',
            title: 'Images téléchargées',
            text: `${response.urls.length} image(s) ajoutée(s)`,
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          Swal.fire('Erreur', response.error || 'Échec du téléchargement', 'error');
          this.galleryPreviewOriginalUrls = [];
        }
      },
      error: (err) => {
        this.isUploadingGallery = false;
        Swal.close();
        console.error('Erreur upload galerie:', err);
        Swal.fire('Erreur', 'Impossible de télécharger les images de la galerie', 'error');
        this.galleryPreviewOriginalUrls = [];
      }
    });
  }

  uploadGalleryImagesViaImagesousproduit(files: File[]): void {
    if (!files || files.length === 0) return;
    this.isUploadingGallery = true;
    this.isAwaitingGalleryValidation = false;
    this.galleryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, 'atelier-gallery-images').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploadingGallery = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryPreviewProcessedUrls = response.urls;
          this.isAwaitingGalleryValidation = true;
        } else {
          Swal.fire('Erreur', (response.errors && response.errors[0]) || 'Échec du traitement', 'error');
          this.galleryPreviewOriginalUrls = [];
        }
      },
      error: () => {
        this.isUploadingGallery = false;
        Swal.fire('Erreur', 'Impossible de traiter les images de la galerie', 'error');
        this.galleryPreviewOriginalUrls = [];
      }
    });
  }

  validateGalleryPreview(): void {
    if (!this.galleryPreviewProcessedUrls.length) return;

    this.galleryImages.push(...this.galleryPreviewProcessedUrls);
    this.atelier.galleryImages = this.galleryImages;

    this.galleryPreviewOriginalUrls = [];
    this.galleryPreviewProcessedUrls = [];
    this.isAwaitingGalleryValidation = false;
    this.selectedGalleryFiles = null;

    if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';

    Swal.fire({
      icon: 'success',
      title: 'Images validées',
      text: `${this.galleryImages.length} image(s) dans la galerie.`,
      timer: 1500,
      showConfirmButton: false
    });
  }

  cancelGalleryPreview(silent: boolean = false): void {
    const urlsToDelete = [...this.galleryPreviewProcessedUrls];

    const doCancel = () => {
      urlsToDelete.forEach(url => {
        this.awsService.deleteFile(url).subscribe({
          next: () => console.log('🗑️ Image refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      });
      this.galleryPreviewOriginalUrls = [];
      this.galleryPreviewProcessedUrls = [];
      this.isAwaitingGalleryValidation = false;
      this.selectedGalleryFiles = null;

      if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser ces images ?',
      text: 'Les images traitées seront supprimées et vous pourrez en choisir d\'autres.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  deleteGalleryImage(index: number): void {
    const imageUrl = this.galleryImages[index];
    Swal.fire({
      title: 'Supprimer cette image ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, supprimer'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await this.awsService.deleteFile(imageUrl).toPromise();
          this.galleryImages.splice(index, 1);
          this.atelier.galleryImages = this.galleryImages;
          Swal.fire('Supprimée', '', 'success');
        } catch (error) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'image', 'error');
        }
      }
    });
  }

  // ==================== VALIDATION ====================
  validateForm(): boolean {
    if (this.isAwaitingMainValidation) {
      Swal.fire('Validation requise', 'Veuillez valider ou refuser l\'image principale.', 'warning');
      return false;
    }
    if (this.isAwaitingGalleryValidation) {
      Swal.fire('Validation requise', 'Veuillez valider ou refuser les images de la galerie.', 'warning');
      return false;
    }
    if (this.isUploadingMain || this.isUploadingGallery) {
      Swal.fire('Téléchargement en cours', 'Veuillez attendre la fin des téléchargements', 'warning');
      return false;
    }
    if (!this.atelier.name?.trim()) {
      Swal.fire('Nom requis', '', 'warning');
      return false;
    }
    if (!this.atelier.description?.trim()) {
      Swal.fire('Description requise', '', 'warning');
      return false;
    }
    if (!this.mainImage) {
      Swal.fire('Image principale requise', '', 'warning');
      return false;
    }
    if (!this.atelier.openingTime || !this.atelier.closingTime) {
      Swal.fire('Horaires requis', '', 'warning');
      return false;
    }
    if (!this.atelier.activeDays || this.atelier.activeDays.length === 0) {
      Swal.fire('Sélectionnez au moins un jour', '', 'warning');
      return false;
    }
    if (!this.atelier.sessionDurations || this.atelier.sessionDurations.length === 0) {
      Swal.fire('Sélectionnez au moins une durée', '', 'warning');
      return false;
    }
    return true;
  }

  // ==================== SUBMIT ====================
  onSubmit(): void {
    if (!this.validateForm()) return;
    if (!this.idAtelier) {
      Swal.fire('Erreur', 'ID atelier manquant', 'error');
      return;
    }

    if (!this.atelier.serviceId) {
      Swal.fire('Erreur', 'Service ID manquant. Veuillez recharger la page.', 'error');
      return;
    }

    this.isSubmitting = true;
    Swal.fire({
      title: 'Mise à jour en cours...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    const atelierData = {
      name: this.atelier.name.trim(),
      namearabe: this.atelier.namearabe?.trim() || '',
      nameanglais: this.atelier.nameanglais?.trim() || '',
      prix: this.atelier.prix?.trim() || '',
      description: this.atelier.description,
      descriptionarabe: this.atelier.descriptionarabe?.trim() || '',
      descriptionanglais: this.atelier.descriptionanglais?.trim() || '',
      mainImageUrl: this.mainImage,
      galleryImages: this.galleryImages,
      openingTime: this.atelier.openingTime,
      closingTime: this.atelier.closingTime,
      activeDays: this.atelier.activeDays,
      sessionDurations: this.atelier.sessionDurations
    };

    this.atelierService.updateAtelier(
      parseInt(this.idAtelier, 10),
      atelierData,
      this.atelier.serviceId
    ).subscribe({
      next: () => {
        Swal.close();
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès !',
          text: 'Atelier mis à jour avec succès',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          if (this.idService) {
            this.router.navigate(['/list-Ateliers', this.idService]);
          } else {
            this.router.navigate(['/listeServices']);
          }
        });
      },
      error: (error: HttpErrorResponse) => {
        Swal.close();
        this.isSubmitting = false;
        let errorMessage = 'Erreur lors de la mise à jour';
        if (error.status === 400) errorMessage = 'Données invalides. Vérifiez les champs.';
        else if (error.status === 401) errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        else if (error.status === 403) errorMessage = 'Permissions insuffisantes.';
        else if (error.status === 404) errorMessage = 'Atelier non trouvé.';
        else if (error.status === 500) errorMessage = 'Erreur serveur. Réessayez plus tard.';
        else if (error.message) errorMessage = error.message;
        Swal.fire('Erreur', errorMessage, 'error');
      }
    });
  }

  // ==================== CANCEL ====================
  cancel(): void {
    if (this.idService) {
      this.router.navigate(['/list-Ateliers', this.idService]);
    } else {
      this.router.navigate(['/listeServices']);
    }
  }

  // ==================== RESET ====================
  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser le formulaire ?',
      text: 'Les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        // Reset previews
        this.mainPreviewOriginalUrl = null;
        this.mainPreviewProcessedUrl = null;
        this.isAwaitingMainValidation = false;
        this.mainImageUploadMode = 'direct';

        this.galleryPreviewOriginalUrls = [];
        this.galleryPreviewProcessedUrls = [];
        this.isAwaitingGalleryValidation = false;
        this.galleryUploadMode = 'direct';

        if (this.idAtelier) {
          this.loadAtelier(parseInt(this.idAtelier, 10));
        }
        Swal.fire('Formulaire réinitialisé', '', 'success');
      }
    });
  }
}